import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import clsx from 'clsx';
import { TiktokDailyRow } from '@/services/tiktok.service';

// One metric at a time, deliberately. Spend (currency) and impressions (tens of
// thousands) share no scale, and a second y-axis is the single worst thing you
// can do to a reader — so the metric is a selector, not a second series.
type Metric = 'spend' | 'impressions' | 'clicks' | 'conversion';

const METRICS: Array<{ id: Metric; label: string }> = [
  { id: 'spend', label: 'Spend' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'conversion', label: 'Conversions' },
];

type Props = {
  rows: TiktokDailyRow[];
  currency?: string;
};

const compact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${(n / 1_000).toFixed(1)}k`
      : `${Math.round(n)}`;

const dayLabel = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export function TiktokTrendChart({ rows, currency }: Props) {
  const [metric, setMetric] = useState<Metric>('spend');

  const active = METRICS.find((m) => m.id === metric)!;
  const isMoney = metric === 'spend';

  const data = useMemo(
    () => rows.map((r) => ({ date: r.date, value: Number(r[metric]) || 0 })),
    [rows, metric],
  );

  const total = useMemo(() => data.reduce((a, d) => a + d.value, 0), [data]);

  // A single point isn't a trend — an area chart drawn through one value reads
  // as a flat line and implies history that doesn't exist.
  if (data.length < 2) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 px-4 py-10 text-center">
        <div className="text-sm font-medium text-text-primary">Not enough history to chart yet</div>
        <div className="mt-1 text-xs text-text-muted">
          {data.length === 0
            ? 'Sync reporting to pull the last 7–30 days of metrics.'
            : 'One day of data so far — the trend fills in as days accumulate.'}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Metric selector sits in one row above the plot */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-xl border border-border bg-surface-alt/60 p-1">
          {METRICS.map((m) => (
            <button
              key={m.id}
              onClick={() => setMetric(m.id)}
              className={clsx(
                'rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
                metric === m.id
                  ? 'bg-surface text-text-primary shadow-soft'
                  : 'text-text-muted hover:text-text-primary',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
        {/* The headline number doubles as the visible-label relief the contrast
            check requires — the mark alone never has to carry the value. */}
        <div className="text-right">
          <div className="text-2xs uppercase tracking-wide text-text-muted">
            {active.label} · {data.length} days
          </div>
          <div className="font-display text-lg font-bold tabular-nums text-text-primary">
            {isMoney
              ? `${currency ? `${currency} ` : ''}${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
              : total.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="tiktokTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-chart-series)" stopOpacity={0.28} />
                <stop offset="100%" stopColor="var(--color-chart-series)" stopOpacity={0.02} />
              </linearGradient>
            </defs>

            {/* Recessive grid: horizontal only, no axis lines, no tick marks. */}
            <CartesianGrid stroke="var(--color-chart-grid)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={dayLabel}
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              tickFormatter={compact}
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              width={44}
            />
            <Tooltip
              cursor={{ stroke: 'var(--color-chart-series)', strokeOpacity: 0.35, strokeWidth: 1 }}
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 12,
                fontSize: 12,
                color: 'var(--color-text-primary)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}
              labelFormatter={(v) => dayLabel(String(v))}
              formatter={(v) => {
                const n = Number(v) || 0;
                return [
                  isMoney
                    ? `${currency ? `${currency} ` : ''}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                    : n.toLocaleString(),
                  active.label,
                ];
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="var(--color-chart-series)"
              strokeWidth={2}
              fill="url(#tiktokTrendFill)"
              // >=8px hit/mark size on hover, per mark specs; hidden otherwise so
              // the line stays thin.
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-surface)' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
